import { inject, Injectable, signal } from '@angular/core';
import { CurrentUserService } from '../../common/current-user/current-user.service';
import { UserAnnouncementViewService } from '../user-announcement-view/user-announcement-view.service';
import {
  APPLICATION_AUTO_DECLINE_ANNOUNCEMENT_KEY,
  resolveExplainerDays,
  reviewWindowExplainerGate,
  type ReviewWindowBrand,
} from './auto-decline-explainer.helpers';

export { APPLICATION_AUTO_DECLINE_ANNOUNCEMENT_KEY } from './auto-decline-explainer.helpers';

@Injectable({
  providedIn: 'root',
})
export class AutoDeclineExplainerService {
  private readonly announcementViews = inject(UserAnnouncementViewService);
  private readonly currentUserService = inject(CurrentUserService);

  readonly isOpen = signal(false);
  readonly autoDeclineDays = signal<number | null>(null);

  private lastCheckedBrandId: string | null = null;
  private inFlightBrandId: string | null = null;

  resetSession(): void {
    this.lastCheckedBrandId = null;
    this.inFlightBrandId = null;
    this.isOpen.set(false);
    this.autoDeclineDays.set(null);
  }

  openIfUnseen(brand: ReviewWindowBrand): void {
    if (!brand?.id) {
      this.resetSession();
      return;
    }

    const gate = reviewWindowExplainerGate(brand);
    if (!gate) {
      return;
    }
    if (this.lastCheckedBrandId === gate.brandId || this.inFlightBrandId === gate.brandId) {
      return;
    }

    this.inFlightBrandId = gate.brandId;
    this.autoDeclineDays.set(gate.days);

    this.announcementViews
      .hasViewed(APPLICATION_AUTO_DECLINE_ANNOUNCEMENT_KEY, gate.brandId)
      .subscribe({
        next: (status) => {
          if (this.inFlightBrandId !== gate.brandId) {
            return;
          }
          this.inFlightBrandId = null;
          this.lastCheckedBrandId = gate.brandId;
          if (!status.viewed) {
            this.isOpen.set(true);
          }
        },
        error: () => {
          if (this.inFlightBrandId === gate.brandId) {
            this.inFlightBrandId = null;
          }
        },
      });
  }

  open(days?: number | null): void {
    const resolvedDays = resolveExplainerDays(
      days,
      this.currentUserService.focussedBrand?.autoDeclineDays,
    );
    if (resolvedDays != null) {
      this.autoDeclineDays.set(resolvedDays);
    }
    this.isOpen.set(true);
  }

  close(): void {
    this.isOpen.set(false);
    const brandId = this.currentUserService.focussedBrand?.id as string | undefined;
    if (!brandId) {
      return;
    }
    this.announcementViews
      .markViewed(APPLICATION_AUTO_DECLINE_ANNOUNCEMENT_KEY, brandId)
      .subscribe({
        error: () => undefined,
      });
  }
}
